import { ComponentPropsWithoutRef } from 'react';
import styled from 'styled-components';
import { breakpoints, FONTSIZE, sizes, theme } from 'theme';

export type ButtonClickHandler =
    | ((event: React.MouseEvent<HTMLButtonElement>) => void)
    | ((event: React.MouseEvent<HTMLButtonElement>) => Promise<void>);

export type ButtonProps = Omit<ComponentPropsWithoutRef<'button'>, 'onClick' | 'color'> & {
    title: string;
    aria: string;
    onClick?: ButtonClickHandler;
    icon?: JSX.Element;
    fullwidth?: boolean;
    submitting?: boolean;
    colour?: ButtonColour;
    iconOnRight?: boolean;
    unfocus?: boolean;
};

export type ButtonColour = 'PRIMARY' | 'SECONDARY' | 'BLACK' | 'DANGER';

export const BUTTON_COLOUR_LOOKUP = {
    PRIMARY: {
        textColour: theme.primaryButtonTextColour,
        borderColour: theme.primaryButtonBackgroundColour,
        backgroundColour: theme.primaryButtonBackgroundColour,
        hoverBackgroundColour: theme.primaryButtonHoverBackgroundColour,
        focusBackgroundColour: theme.buttonFocusBackgroundColour,
    },
    SECONDARY: {
        textColour: theme.secondaryButtonTextColour,
        borderColour: theme.secondaryButtonBorderColour,
        backgroundColour: theme.secondaryButtonBackgroundColour,
        hoverBackgroundColour: theme.secondaryButtonHoverBackgroundColour,
        focusBackgroundColour: theme.buttonFocusBackgroundColour,
    },
    BLACK: {
        textColour: theme.blackButtonTextColour,
        borderColour: theme.blackButtonBorderColour,
        backgroundColour: theme.secondaryButtonBackgroundColour,
        hoverBackgroundColour: theme.secondaryButtonHoverBackgroundColour,
        focusBackgroundColour: theme.buttonFocusBackgroundColour,
    },
    DANGER: {
        textColour: theme.errorColour,
        borderColour: theme.errorColour,
        backgroundColour: theme.secondaryButtonBackgroundColour,
        hoverBackgroundColour: theme.secondaryButtonHoverBackgroundColour,
        focusBackgroundColour: theme.buttonFocusBackgroundColour,
    },
};

export const StyledButton = styled.button<{
    disabled?: boolean;
    $fullwidth?: boolean;
    $colourType: ButtonColour;
}>`
    display: flex;
    gap: ${sizes.spacingSm};
    align-items: center;
    justify-content: center;

    height: 48px;
    padding: 0 ${sizes.spacingMd};
    border-radius: 24px;
    cursor: pointer;
    white-space: nowrap;

    font-family: ${theme.fontFamilyMain};
    font-size: ${FONTSIZE.BodyRegular};
    font-weight: bold;

    width: ${({ $fullwidth }) => {
        return $fullwidth ? '100%' : 'fit-content';
    }};
    @media (max-width: ${breakpoints.sm}) {
        width: ${({ $fullwidth }) => {
            //mobile buttons are full width by default unless overridden with false
            return $fullwidth === false ? 'fit-content' : '100%';
        }};
    }

    color: ${({ disabled, $colourType }) => {
        return disabled ? theme.textSecondaryColour : BUTTON_COLOUR_LOOKUP[$colourType].textColour;
    }};
    background-color: ${({ disabled, $colourType }) => {
        return disabled ? theme.buttonDisabledBackgroundColour : BUTTON_COLOUR_LOOKUP[$colourType].backgroundColour;
    }};
    border: ${({ $colourType }) => {
        return `2px solid ${BUTTON_COLOUR_LOOKUP[$colourType].borderColour}`;
    }};

    &:hover {
        background-color: ${({ disabled, $colourType }) => {
            return disabled ? null : BUTTON_COLOUR_LOOKUP[$colourType].hoverBackgroundColour;
        }};
    }
    &:focus {
        background-color: ${({ $colourType }) => {
            return BUTTON_COLOUR_LOOKUP[$colourType].focusBackgroundColour;
        }};
        color: ${theme.buttonFocusTextColour};
    }
`;
